import { apiClient, apiClientPublic } from 'services/api-client';

const TC_API = '/tc';

// ============ PUBLIC APIs (no auth) ============

/**
 * Get public transparency overview for a company
 * @param {string} companyKey - Company slug identifier
 * @returns {Promise} Public overview data
 */
export const getPublicOverview = async (companyKey) => {
  return await apiClientPublic.get(`${TC_API}/public/${companyKey}/overview`);
};

/**
 * Get public framework badges for a company
 * @param {string} companyKey - Company slug identifier
 * @returns {Promise} Framework badges data
 */
export const getPublicFrameworks = async (companyKey) => {
  return await apiClientPublic.get(`${TC_API}/public/${companyKey}/frameworks`);
};

/**
 * Get public governance structure for a company
 * @param {string} companyKey - Company slug identifier
 * @returns {Promise} Governance structure data
 */
export const getPublicGovernance = async (companyKey) => {
  return await apiClientPublic.get(`${TC_API}/public/${companyKey}/governance`);
};

/**
 * Get public policies for a company
 * @param {string} companyKey - Company slug identifier
 * @returns {Promise} Public policies data
 */
export const getPublicPolicies = async (companyKey) => {
  return await apiClientPublic.get(`${TC_API}/public/${companyKey}/policies`);
};

// ============ PROTECTED APIs (auth or token) ============

/**
 * Get protected transparency data for a company
 * @param {string} companyKey - Company slug identifier
 * @param {string|null} token - Optional access token for external viewers
 * @returns {Promise} Protected transparency data
 */
export const getProtectedTransparency = async (companyKey, token = null) => {
  if (token) {
    return await apiClientPublic.get(
      `${TC_API}/protected/`,
      {
        params: { companyKey },
        headers: { 'X-Access-Token': token }
      }
    );
  }
  return await apiClient.get(`${TC_API}/protected/`, { params: { companyKey } });
};

/**
 * Get protected frameworks with maturity details
 * @param {string} companyKey - Company slug identifier
 * @param {string|null} token - Optional access token for external viewers
 * @returns {Promise} Frameworks data with maturity details
 */
export const getProtectedFrameworks = async (companyKey, token = null) => {
  if (token) {
    return await apiClientPublic.get(
      `${TC_API}/protected/frameworks`,
      {
        params: { companyKey },
        headers: { 'X-Access-Token': token }
      }
    );
  }
  return await apiClient.get(`${TC_API}/protected/frameworks`, { params: { companyKey } });
};

/**
 * Get framework requirements (drill-down)
 * @param {string} frameworkId - Framework identifier
 * @param {string} companyKey - Company slug identifier
 * @param {string|null} token - Optional access token for external viewers
 * @returns {Promise} Framework requirements data
 */
export const getFrameworkRequirements = async (frameworkId, companyKey, token = null) => {
  if (token) {
    return await apiClientPublic.get(
      `${TC_API}/protected/frameworks/${frameworkId}/requirements`,
      {
        params: { companyKey },
        headers: { 'X-Access-Token': token }
      }
    );
  }
  return await apiClient.get(
    `${TC_API}/protected/frameworks/${frameworkId}/requirements`,
    { params: { companyKey } }
  );
};

/**
 * Get governance domains (full hierarchy)
 * @param {string} companyKey - Company slug identifier
 * @param {string|null} token - Optional access token for external viewers
 * @returns {Promise} Governance domains data
 */
export const getGovernanceDomains = async (companyKey, token = null) => {
  if (token) {
    return await apiClientPublic.get(
      `${TC_API}/protected/domains`,
      {
        params: { companyKey },
        headers: { 'X-Access-Token': token }
      }
    );
  }
  return await apiClient.get(`${TC_API}/protected/domains`, { params: { companyKey } });
};

/**
 * Get protected policy details
 * @param {string} policyId - Policy identifier
 * @param {string} companyKey - Company slug identifier
 * @param {string|null} token - Optional access token for external viewers
 * @returns {Promise} Policy details data
 */
export const getProtectedPolicy = async (policyId, companyKey, token = null) => {
  if (token) {
    return await apiClientPublic.get(
      `${TC_API}/protected/policies/${policyId}`,
      {
        params: { companyKey },
        headers: { 'X-Access-Token': token }
      }
    );
  }
  return await apiClient.get(
    `${TC_API}/protected/policies/${policyId}`,
    { params: { companyKey } }
  );
};

// ============ PREVIEW APIs (internal use, no logging) ============

/**
 * Get preview of public transparency view (internal users only)
 * Does not generate audit logs
 * @param {string} companyKey - Company slug identifier
 * @returns {Promise} Preview data
 */
export const getPreviewPublic = async (companyKey) => {
  return await apiClient.get(`${TC_API}/preview/public`, { params: { companyKey } });
};

/**
 * Get preview of protected transparency view (internal users only)
 * Does not generate audit logs
 * @param {string} companyKey - Company slug identifier
 * @returns {Promise} Preview data
 */
export const getPreviewProtected = async (companyKey) => {
  return await apiClient.get(`${TC_API}/preview/protected`, { params: { companyKey } });
};
